import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMXN, fmtTime, fmtDateOnly } from "@/lib/api";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Card, CardContent } from "@/components/ui/card";
import OrderStateBadge from "@/components/OrderStateBadge";
import { Clock, ChefHat, BadgeDollarSign, Receipt, TrendingUp, TrendingDown, Wallet, Activity, Users, Layers, LayoutGrid, AlertTriangle, RotateCcw } from "lucide-react";

function Stat({ icon: Icon, label, value, sub, tone = "default" }) {
  const tones = { default: "", primary: "text-primary", success: "text-emerald-500", danger: "text-red-500" };
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={`mt-2 text-3xl font-black ${tones[tone]}`}>{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
          </div>
          <div className="rounded-lg bg-secondary p-2.5"><Icon className="h-5 w-5 text-foreground/70" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const fetch = () => api.get("/dashboard/operativo").then((r) => setData(r.data));
    fetch();
    const interval = setInterval(fetch, 15000);
    return () => clearInterval(interval);
  }, []);
  useRealtimeEvents(
    (msg) => { if (msg?.type?.startsWith("orden.")) api.get("/dashboard/operativo").then((r) => setData(r.data)); },
    () => api.get("/dashboard/operativo").then((r) => setData(r.data)),
  );
  if (!data) return <div className="text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-black tracking-tight">Operación de hoy</h2>
        <p className="text-sm text-muted-foreground mt-1">Vista en tiempo real (actualización cada 15s).</p>
      </div>

      {data.abiertas_dias_anteriores > 0 && (
        <Link to="/ordenes" className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-50 dark:bg-amber-950/30 px-4 py-2 text-sm font-semibold text-amber-700 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors" data-testid="alerta-ordenes-anteriores">
          <AlertTriangle className="h-4 w-4" />
          Existen {data.abiertas_dias_anteriores} {data.abiertas_dias_anteriores === 1 ? "orden abierta" : "órdenes abiertas"} de días anteriores. Revisa.
        </Link>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Receipt} label="Órdenes abiertas" value={data.abiertas} tone="primary" />
        <Stat icon={ChefHat} label="En preparación" value={data.en_preparacion} />
        <Stat icon={Activity} label="Listas" value={data.listas} tone="success" />
        <Stat icon={BadgeDollarSign} label="Cobradas hoy" value={data.cobradas_hoy} />
        <Stat icon={TrendingUp} label="Venta del día" value={fmtMXN(data.venta_hoy)} tone="primary" />
        <Stat icon={TrendingDown} label="Gastos del día" value={fmtMXN(data.gastos_dia)} tone="danger" />
        <Stat icon={RotateCcw} label="Reembolsos del día" value={fmtMXN(data.reembolsos_dia || 0)} tone="danger" sub={data.reembolsos_count ? `${data.reembolsos_count} ${data.reembolsos_count === 1 ? "orden" : "órdenes"}` : "Sin reembolsos"} />
        <Stat icon={Wallet} label="Neto estimado" value={fmtMXN(data.neto)} tone="success" />
        <Stat icon={Clock} label="Hora actual" value={new Date().toLocaleTimeString("es-MX", { timeZone: "America/Mexico_City", hour: "2-digit", minute: "2-digit" })} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Stat icon={Users} label="Clientes atendidos" value={data.clientes_atendidos ?? 0} tone="primary" sub="del día" />
        <Stat icon={LayoutGrid} label="Mesas ocupadas" value={`${data.mesas_ocupadas ?? 0} / ${data.mesas_total ?? 0}`} sub="en tiempo real" />
        <Card data-testid="ventas-por-canal-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Ventas por canal</div>
              <div className="rounded-lg bg-secondary p-2.5"><Layers className="h-5 w-5 text-foreground/70" /></div>
            </div>
            {(!data.ventas_por_canal || data.ventas_por_canal.length === 0) ? (
              <div className="mt-3 text-sm text-muted-foreground">Sin ventas registradas hoy.</div>
            ) : (
              <div className="mt-3 space-y-1.5 text-sm">
                {data.ventas_por_canal.map((c) => (
                  <div key={c.key} className="flex items-center justify-between border-b border-border last:border-0 pb-1">
                    <span className="font-semibold">{c.canal}</span>
                    <span className="text-muted-foreground text-xs mr-2">x{c.count}</span>
                    <span className="font-bold">{fmtMXN(c.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-bold mb-4">Órdenes recientes</h3>
          {data.ordenes_recientes.length === 0 && <div className="text-sm text-muted-foreground">Sin órdenes hoy.</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="text-left py-2">Número</th><th className="text-left">Tipo</th><th className="text-left">Mesa / Cliente</th><th className="text-left">Estado</th><th className="text-right">Total</th><th className="text-right">Fecha</th><th className="text-right">Hora</th></tr>
              </thead>
              <tbody>
                {data.ordenes_recientes.map((o) => (
                  <tr key={o.id} className="border-t border-border">
                    <td className="py-2 font-bold"><Link to={`/orden/${o.id}`} className="hover:text-primary">{o.numero}</Link></td>
                    <td className="text-muted-foreground">{o.tipo}</td>
                    <td>{o.mesa_nombre || o.cliente_nombre || "-"}</td>
                    <td><OrderStateBadge state={o.estado} /></td>
                    <td className="text-right font-semibold">{fmtMXN(o.total)}</td>
                    <td className="text-right text-muted-foreground whitespace-nowrap" data-testid={`dash-orden-fecha-${o.id}`}>{fmtDateOnly(o.created_at)}</td>
                    <td className="text-right text-muted-foreground whitespace-nowrap" data-testid={`dash-orden-hora-${o.id}`}>{fmtTime(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
